import { Badge, Group, Switch, UnstyledButton } from "@mantine/core";
import { ReactElement, useCallback, useState } from "react";
import { ISwitchWithLabelsProps } from "./types";
import DisabledContainer from "@common/components/layout/DisabledContainer";

export default function SwitchWithLabels({
  "data-testid": dataTestId,
  disabled,
  labelOn,
  labelOff,
  badge,
  value,
  onChange,
  size,
  fz,
  m,
  w,
}: ISwitchWithLabelsProps): ReactElement {
  const [isChecked, setIsChecked] = useState(value);

  const onClick = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      onChange?.(checked);
    },
    [setIsChecked, onChange],
  );

  const theSize = size || "sm";
  const theTextSize = fz || theSize;

  return (
    <Group
      data-testid={dataTestId}
      align="center"
      display="inline-flex"
      spacing="xs"
      noWrap
      m={m}
      pos="relative"
      w={w}
    >
      <DisabledContainer disabled={disabled}>
        <UnstyledButton
          data-testid={`${dataTestId}_off`}
          disabled={disabled}
          c={isChecked ? "gray.8" : "brand.4"}
          fw={isChecked ? undefined : 700}
          fz={theTextSize}
          onClick={() => onClick(!isChecked)}
        >
          {labelOff}
        </UnstyledButton>
      </DisabledContainer>
      <Switch
        data-testid={`${dataTestId}_switch`}
        checked={value}
        color="brand"
        disabled={disabled}
        onChange={({ currentTarget: { checked } }) => onClick(checked)}
        size={theSize}
      />
      <DisabledContainer disabled={disabled}>
        <UnstyledButton
          data-testid={`${dataTestId}_on`}
          disabled={disabled}
          c={isChecked ? "brand.4" : "gray.8"}
          fw={isChecked ? 700 : undefined}
          fz={theTextSize}
          onClick={() => onClick(!isChecked)}
        >
          {labelOn}
        </UnstyledButton>
      </DisabledContainer>

      {badge && (
        <Badge
          data-testid={`${dataTestId}_badge`}
          color="green.1"
          radius="sm"
          variant="outline"
          bg="green.0"
          c="green.1"
          fw={700}
          fz={theTextSize}
          left={{ base: "auto", sm: "100%" }}
          lts={-0.25}
          ml={{ base: 0, sm: "xs" }}
          pos={{ base: "relative", sm: "absolute" }}
          tt="none"
        >
          {badge}
        </Badge>
      )}
    </Group>
  );
}
